import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { LoadingService } from "./loading.service";
import { catchError, finalize } from "rxjs/operators";
import { inject } from "@angular/core";
import { throwError } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { HTTP_CONSTANTS, HTTP_STATUS_CONSTANTS } from "../common/constants";
import { MESSAGES_CONSTANTS } from "../common/messages-constants";
import { NUMBERS_CONSTANTS } from "../common/numbers-constants";

export const CORRELATION_ID_HEADER = HTTP_CONSTANTS.correlationIdHeader;

// Generates a per-request correlation id and attaches it as a header so it can
// be correlated with the same id echoed back on the response and in API logs.
function generateCorrelationId(): string {
  return typeof crypto !== HTTP_CONSTANTS.undefinedType && HTTP_CONSTANTS.cryptoRandomUuidMethod in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(NUMBERS_CONSTANTS.sixteen).slice(NUMBERS_CONSTANTS.two)}`;
}

// Maps a failed HTTP response to an actionable, user-facing message.
export function resolveErrorMessage(err: HttpErrorResponse): string {
  if (err.status === HTTP_STATUS_CONSTANTS.networkError) {
    return MESSAGES_CONSTANTS.httpUnableToReachServer;
  }

  switch (err.status) {
    case HTTP_STATUS_CONSTANTS.badRequest:
      return err.error?.displayMessage || MESSAGES_CONSTANTS.httpCheckInputAndTryAgain;
    case HTTP_STATUS_CONSTANTS.unauthorized:
      return MESSAGES_CONSTANTS.httpSessionExpiredSignInAgain;
    case HTTP_STATUS_CONSTANTS.forbidden:
      return MESSAGES_CONSTANTS.httpNoPermissionForAction;
    case HTTP_STATUS_CONSTANTS.notFound:
      return MESSAGES_CONSTANTS.httpResourceNotFound;
    case HTTP_STATUS_CONSTANTS.internalServerError:
      return MESSAGES_CONSTANTS.httpSomethingWentWrongTryAgain;
    default:
      return MESSAGES_CONSTANTS.httpUnexpectedErrorTryAgain;
  }
}

// Loading Spinner + centralized HTTP error handling interceptor
export const loadingSpinnerInterceptorFunctional: HttpInterceptorFn = (req, next) => {
  const toastrService = inject(ToastrService);
  const loadingService = inject(LoadingService); // Instantiate the loading service
  loadingService.ShowLoadingSpinner(); // Show loading spinner UI element

  const correlationId = generateCorrelationId();
  req = req.clone({ setHeaders: { [CORRELATION_ID_HEADER]: correlationId } });

  return next(req).pipe(

    catchError((err: HttpErrorResponse) => {
      const responseCorrelationId = err.headers?.get(CORRELATION_ID_HEADER) ?? correlationId;
      const message = resolveErrorMessage(err);

      toastrService.error(message);
      console.error(`[HTTP ${err.status}] ${message} (correlationId: ${responseCorrelationId})`, err);

      return throwError(() => err);
    }),
    finalize(() => {
      loadingService.HideLoadingSpinner(); // Hide loading spinner UI element
    })
  );
};